import React, { useCallback, useRef, useEffect } from 'react';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { useClientUser } from '@/hooks';
import { useNavigate } from 'react-router-dom';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useSticky } from 'react-table-sticky';
import InfiniteLoader from 'react-window-infinite-loader';
import CircularProgress from '@material-ui/core/CircularProgress';
import FlagIcon from '@material-ui/icons/Flag';
import FlagOutlinedIcon from '@material-ui/icons/FlagOutlined';
import { useTable, useFlexLayout, useSortBy, useRowSelect } from 'react-table';

import { TableContainer } from '@material-ui/core';
import HeaderCell from '@/components/CarrierAccountingOrders/table/HeaderCell';
import BodyCell from '@/components/CarrierAccountingOrders/table/BodyCell';
import BodyRow from '@/components/CarrierAccountingOrders/table/BodyRow';
import { IndeterminateCheckbox } from '@/components/DispatchUnassigned/table';

const Table = ({ state, loading, callbacks = {} }) => {
    const inf = useRef(null);
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        rows,
        state: tableState,
        setSortBy,
    } = useTable(
        {
            columns: state.columns,
            data: state.invoices,
            getRowId: callbacks.getRowId,
            enableRowSelection: true,
            enableMultiRowSelection: true,
            disableSortBy: true,
        },
        useSortBy,
        useRowSelect,
        useSticky,
        useFlexLayout,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                ...(callbacks.flagRowToggle
                    ? [
                          {
                              id: 'flag',
                              sticky: 'left',
                              width: 50,
                              Header: () => <div />,
                              Cell: ({ row }) => {
                                  const invoice = row.original;

                                  if (invoice.flagged) {
                                      return (
                                          <FlagIcon
                                              css={css`
                                                  color: #d23e3e;
                                              `}
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  callbacks.flagRowToggle(invoice);
                                              }}
                                          />
                                      );
                                  }

                                  return (
                                      <FlagOutlinedIcon
                                          onClick={(e) => {
                                              e.stopPropagation();
                                              callbacks.flagRowToggle(invoice);
                                          }}
                                      />
                                  );
                              },
                          },
                      ]
                    : []),
                {
                    id: 'selection',
                    sticky: 'left',
                    width: 50,
                    Header: ({ getToggleAllRowsSelectedProps }) => (
                        <IndeterminateCheckbox
                            {...getToggleAllRowsSelectedProps()}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                        />
                    ),
                    Cell: ({ row }) => (
                        <IndeterminateCheckbox
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            {...row.getToggleRowSelectedProps()}
                        />
                    ),
                },
                ...columns,
            ]);
        }
    );

    useEffect(() => {
        callbacks.selectRows(tableState.selectedRowIds);
    }, [tableState.selectedRowIds]);

    const Row = useCallback(
        ({ index, style }) => {
            //HEADERS
            if (index === 0) {
                const [group] = headerGroups;

                return (
                    <div {...group.getHeaderGroupProps({ style })}>
                        {group.headers.reduce(
                            (acc, subgroup) => [
                                ...acc,
                                subgroup.headers.map((col) => {
                                    return (
                                        <HeaderCell
                                            {...col.getHeaderProps()}
                                            key={col.id}
                                            cell={col}
                                            align={col.align}
                                            css={css`
                                                height: 50px !important;
                                            `}
                                        />
                                    );
                                }),
                            ],
                            []
                        )}
                    </div>
                );
            }

            //BODY
            const row = rows[index - 1];
            if (row) {
                prepareRow(row);

                return (
                    <BodyRow
                        {...row.getRowProps({
                            style,
                        })}
                        row={row}
                        onClick={() => callbacks.onRowClick(row.original)}
                    >
                        {row.cells.map((cell) => (
                            <BodyCell
                                {...cell.getCellProps()}
                                cell={cell}
                                align={cell.column.align}
                                span={cell.column.span}
                                callbacks={callbacks}
                                css={css`
                                    overflow: hidden;
                                    height: 50px !important;
                                `}
                            />
                        ))}
                    </BodyRow>
                );
            }

            return (
                <BodyRow
                    style={style}
                    css={css`
                        align-items: center;
                        display: flex;
                        justify-content: center;
                    `}
                >
                    {loading?.init ? (
                        <CircularProgress
                            size={24}
                            css={css`
                                color: ${colors.greys[4]};
                            `}
                        />
                    ) : (
                        <div>No results found</div>
                    )}
                </BodyRow>
            );
        },
        [prepareRow, rows, loading]
    );

    return (
        <TableContainer
            css={css`
                height: 100%;
                overflow: hidden;
                background: #fff;
            `}
        >
            <div
                {...getTableProps()}
                css={css`
                    height: 100%;
                    display: flex;
                    flex-grow: 1;
                    flex-direction: column;
                `}
            >
                <div
                    {...getTableBodyProps()}
                    css={css`
                        display: flex;
                        flex-grow: 1;
                    `}
                >
                    <InfiniteLoader
                        ref={inf}
                        itemCount={state.hasMore ? rows.length + 2 : rows.length + 1}
                        threshold={25}
                        isItemLoaded={(idx) => {
                            return idx < state.invoices.length;
                        }}
                        loadMoreItems={(start, stop) => {
                            if (!loading.init && state.hasMore) {
                                callbacks.loadMore();
                            }
                        }}
                    >
                        {({ onItemsRendered, ref }) => {
                            return (
                                <AutoSizer>
                                    {({ height, width }) => (
                                        <FixedSizeList
                                            ref={ref}
                                            onItemsRendered={onItemsRendered}
                                            height={height}
                                            itemCount={state.hasMore ? rows.length + 2 : rows.length + 1}
                                            itemSize={50}
                                            width={width}
                                        >
                                            {Row}
                                        </FixedSizeList>
                                    )}
                                </AutoSizer>
                            );
                        }}
                    </InfiniteLoader>
                </div>
            </div>
        </TableContainer>
    );
};

export default Table;
